'use client'

import { legacyHookAttrs } from '@/msgdeskRuntime/legacyBridge/SelectorContracts'
import {
  selectAccessGrant,
  selectAccessState,
  selectDeskIdentity,
  selectPageSettingsState,
  selectRootFeedIds,
  selectSectionPaging,
  selectSubRenderState,
  type SubsStoreState,
} from '@/msgdeskRuntime/state'
import { useSubsStore } from '@/state/MsgTreeProvider'
import { usePanelsSurface } from '@/deskUiSkeletons/Page/PanelsSurfaceContext'
import { SubRow } from '@/deskUiSkeletons/subs/SubRow'
import type { MusicSurfaceKind } from './MusicProviders'

function musicKind(value: unknown): 'track' | 'box' | 'comment' {
  const normalized = String(value || '').toLowerCase()
  if (normalized === 'music_track' || normalized === 'musictrack') return 'track'
  if (normalized === 'music_box' || normalized === 'musicbox') return 'box'
  return 'comment'
}

export function MusicAnswersPanel({ kind }: { kind: MusicSurfaceKind }) {
  const { state } = useSubsStore()
  const store = state as SubsStoreState
  const { FoldSubsButton, PagingBar, RefreshSubs, CheckedsInfo, MoreLink, config, skin } = usePanelsSurface()
  const access = selectAccessState(store)
  const settings = selectPageSettingsState(store)
  const panelMode = state.renderProjection.mode === 'answers' ? 'answers' : 'msg'
  const rootIds = selectRootFeedIds(store, false, panelMode)
  const contentIds: number[] = []
  const commentIds: number[] = []
  for (const id of rootIds) {
    const rowKind = musicKind(selectSubRenderState(store, id)?.msgType)
    if (rowKind === 'track' || (kind === 'musicbox' && rowKind === 'box')) contentIds.push(id)
    else commentIds.push(id)
  }

  const { total, currentPage, totalPages, moreCount } = selectSectionPaging(store, false)
  const isMainMode = state.renderProjection.mode === 'msg'
  const isFolded = isMainMode && currentPage === 1
    && (settings.foldAnswers || state.chromeByContract['subs.answers.rootList']?.visible === false)
  const visible = access.showUnlocked && state.renderProjection.mode !== 'submsges'
  const canCheck = selectAccessGrant(store).capabilities.can_check_or_move
  const variant = config.mainTemplateVariant('answers')

  const panel = (
    <div {...legacyHookAttrs('subs.answers.front', `comments md-subs-panel md-subs-panel--answers ${skin.panelAnswers}`)} style={{ display: visible ? 'block' : 'none' }}>
      {isMainMode
        ? <FoldSubsButton section={0} label="Music and comments" count={total || undefined} />
        : <span className={skin.sectionInfo}>Music and comments<span className={skin.sectionInfoCount}>({total})</span></span>}
      <div {...legacyHookAttrs('subs.panelTools', `md-subs-panel__tools ${skin.panelTools}`)}>
        <RefreshSubs section={0} />
        {canCheck ? <CheckedsInfo section={0} visual="legacy" /> : <span hidden><CheckedsInfo section={0} visual="legacy" /></span>}
      </div>
      <PagingBar section={0} currentPage={currentPage} totalPages={totalPages} />
      <div {...legacyHookAttrs('subs.answers.rootList', `page1 md-subs-list md-subs-list--answers ${skin.rootList}`)} style={{ display: isFolded ? 'none' : undefined }}>
        {contentIds.length ? (
          <section className="mb-5">
            <h2 className="mb-3 text-lg font-semibold text-[#2f241d]">{kind === 'musicbox' ? 'Tracks and boxes' : 'Related tracks'}</h2>
            <ul className="flex list-none flex-col gap-3 p-0">{contentIds.map(id => <SubRow key={id} elId={id} variant={variant} sourceMode={panelMode} />)}</ul>
          </section>
        ) : null}
        {commentIds.length ? (
          <section className="border-t border-[#e1d5c8] pt-5">
            <h2 className="mb-3 text-lg font-semibold text-[#2f241d]">Comments</h2>
            <ul className="flex list-none flex-col gap-3 p-0">{commentIds.map(id => <SubRow key={id} elId={id} variant={variant} sourceMode={panelMode} />)}</ul>
          </section>
        ) : null}
      </div>
      {isFolded ? null : <MoreLink section={0} moreCount={moreCount} />}
    </div>
  )

  if (isMainMode && total < 1) return <div aria-hidden="true" style={{ display: 'none' }}>{panel}</div>
  return panel
}

export function MusicBoxesPanel() {
  const { state } = useSubsStore()
  const store = state as SubsStoreState
  const { FoldSubsButton, PagingBar, RefreshSubs, CheckedsInfo, MoreLink, config, skin } = usePanelsSurface()
  const { canHaveSubmessages } = selectDeskIdentity(store)
  const access = selectAccessState(store)
  const settings = selectPageSettingsState(store)
  const panelMode = state.renderProjection.mode === 'submsges' ? 'submsges' : 'msg'
  const rootIds = selectRootFeedIds(store, true, panelMode)
  const { total, currentPage, totalPages, moreCount } = selectSectionPaging(store, true)
  const isMainMode = state.renderProjection.mode === 'msg'
  const isFolded = isMainMode && currentPage === 1
    && (settings.foldSubmsges || state.chromeByContract['subs.submsges.rootList']?.visible === false)
  const visible = canHaveSubmessages && access.showUnlocked && state.renderProjection.mode !== 'answers'
  const canCheck = selectAccessGrant(store).capabilities.can_check_or_move
  const variant = config.mainTemplateVariant('submsges')

  const panel = (
    <div {...legacyHookAttrs('subs.submsges.front', `compact md-subs-panel md-subs-panel--submsges ${skin.panelSubmsges}`)} style={{ display: visible ? 'block' : 'none' }}>
      {isMainMode
        ? <FoldSubsButton section={1} label="Nested boxes" count={total || undefined} />
        : <span className={skin.sectionInfo}>Nested boxes<span className={skin.sectionInfoCount}>({total})</span></span>}
      <div {...legacyHookAttrs('subs.panelTools', `md-subs-panel__tools ${skin.panelTools}`)}>
        <RefreshSubs section={1} />
        {canCheck ? <CheckedsInfo section={1} visual="legacy" /> : <span hidden><CheckedsInfo section={1} visual="legacy" /></span>}
      </div>
      <PagingBar section={1} currentPage={currentPage} totalPages={totalPages} />
      <ul {...legacyHookAttrs('subs.submsges.rootList', `page1 md-subs-list md-subs-list--submsges ${skin.rootList}`)} style={{ display: isFolded ? 'none' : undefined }}>
        {rootIds.map(id => <SubRow key={id} elId={id} variant={variant} sourceMode={panelMode} />)}
      </ul>
      {isFolded ? null : <MoreLink section={1} moreCount={moreCount} />}
    </div>
  )

  if (!canHaveSubmessages || (isMainMode && total < 1)) return <div aria-hidden="true" style={{ display: 'none' }}>{panel}</div>
  return panel
}
